/**
 * Route guard for the patient follow-ups queue
 * (`/dashboard/coc/patient-followups`) -- FOLLOWUP-OWNERSHIP-ROUTING-
 * CONFIG-V1. Mirrors RequireManagerTracking: the pure decision lives in
 * decideFollowupQueueAccess (followup-queue-access.ts) and keys off the
 * server-computed `followupQueueEnabled` /me door signal (the org's
 * followup_ownership config decides WHO reaches the queue -- e.g. Partha's
 * front desk during the window and the doctor read-only) with the 'coc'
 * nav section preserved as the fail-same legacy arm.
 *
 * This REPLACES RequireNavSection section="coc" for THIS ONE route so a
 * config-admitted role (receptionist / doctor at a configured org) reaches
 * the queue WITHOUT inheriting the COC nav menu (the RequireManagerTracking
 * decoupling precedent). Every 'coc'-section caller renders exactly as
 * before. Backend route auth remains the real security boundary.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideFollowupQueueAccess } from '@/dashboard/coc/followup-queue-access';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireFollowupQueueViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireFollowupQueueView({
  loading,
  error,
  me,
  children,
}: RequireFollowupQueueViewProps): JSX.Element | null {
  const decision = decideFollowupQueueAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireFollowupQueue(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireFollowupQueueView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
